import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Container,
  Button,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useEffect, useState } from "react";
import {
  listTasks,
  getTaskFormFields,
  getTaskVariables,
  completeTaskV2,
  signAndCompleteTaskOtp,
  resendOtpTask,
} from "../../../../api/bpm/endpoints";
import { findNextTask } from "../../../../utils/bpm/nextTask";
import { useAuth } from "../../../../auth";
import { TaskFormRenderer } from "../../../../components/TaskExecution/TaskFormRenderer";
import { PasswordModal } from "../../../../components/TaskExecution/PasswordModal";
import { ResendOtpModal } from "../../../../components/TaskExecution/ResendOtpModal";
import { decodeNavParams } from "../../../../utils/navParams";
import { AssignTaskDialog } from "../../../../components/Tasks/AssignTaskDialog";
import { ForcedTerminationDialog } from "../../../../components/TaskExecution/ForcedTerminationDialog";
import { forceCancelTask } from "../../../../api/bpm/customEndpoints";
import { useQueryClient } from "@tanstack/react-query";

import { usePageTitle } from "../../../../contexts/PageTitleContext";
import { useTranslation } from "react-i18next";

const taskExecutionSearchSchema = z.object({
  from: z
    .enum(["tasks", "all-tasks", "processes", "start"])
    .optional()
    .catch("tasks"),
  fromParams: z.string().optional().catch(""),
  processInstanceId: z.string().optional().catch(""),
});

export const Route = createFileRoute("/__auth/tasks/$taskId/execute")({
  component: TaskExecutionWrapper,
  validateSearch: taskExecutionSearchSchema,
});

function TaskExecutionWrapper() {
  const { taskId } = Route.useParams();
  return <TaskExecutionPage key={taskId} />;
}

function TaskExecutionPage() {
  const { t } = useTranslation();
  const { taskId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { setTitlePrefix } = usePageTitle();
  const queryClient = useQueryClient();

  const [loadingNext, setLoadingNext] = useState(false);
  const [nup, setNup] = useState<string>("");
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const [taskForm, setTaskForm] = useState<any>(null);
  const [taskVariables, setTaskVariables] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [task, setTask] = useState<any>(null);

  const [token, setToken] = useState<string | null>(null);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isResendOtpModalOpen, setIsResendOtpModalOpen] = useState(false);
  const [signatureData, setSignatureData] = useState<any>(null);
  const [storedProcInstId, setStoredProcInstId] = useState<string>("");
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [forcedTerminationOpen, setForcedTerminationOpen] = useState(false);
  const [isForcedTerminating, setIsForcedTerminating] = useState(false);

  useEffect(() => {
    const getStoredToken = () => {
      try {
        const ls = localStorage.getItem("token");
        if (ls) return JSON.parse(ls);
        const ss = sessionStorage.getItem("token");
        if (ss) return JSON.parse(ss);
      } catch {
      }
      return null;
    };

    const stored = getStoredToken();
    if (stored?.accessToken) {
      setToken(stored.accessToken);
    }
  }, []);

  useEffect(() => {
    setTitlePrefix(nup || null);
    return () => setTitlePrefix(null);
  }, [nup, setTitlePrefix]);

  useEffect(() => {
    const fetchTaskDetails = async () => {
      if (!taskId || !user?.tenant) return;

      try {
        const response = await listTasks(
          {
            size: 1,
            byTaskId: taskId,
          } as any,
          { headers: { tenant: user.tenant } },
        );
        const resData = (response as any)?.data || response;

        if (resData.content && resData.content.length > 0) {
          const taskData = resData.content[0];
          setTask(taskData);
          if (taskData.processInstanceId) {
            setStoredProcInstId(taskData.processInstanceId);
          } else if (search.processInstanceId) {
            setStoredProcInstId(search.processInstanceId);
          }

          const nupValue =
            (taskData as any).processInstanceNup || (taskData as any).nup;
          if (nupValue) {
            setNup(nupValue);
          }
        }
      } catch (err) {
        console.error("[TaskExecute] Error fetching task details:", err);
      }
    };

    fetchTaskDetails();
  }, [taskId, user?.tenant, refreshTrigger]);

  useEffect(() => {
    const fetchData = async () => {
      if (!taskId || !user?.tenant) return;

      setLoadingData(true);
      try {
        const [formDataRaw, variablesDataRaw] = await Promise.all([
          getTaskFormFields(taskId, {
            headers: {
              "x-api-key": "7b9nBSEbAYorYo0os2vpW3CaHrg8A3jaceA5eqik" as any,
            },
          }),
          getTaskVariables(user.tenant, taskId, {
            headers: {
              "x-api-key": "7b9nBSEbAYorYo0os2vpW3CaHrg8A3jaceA5eqik" as any,
            },
          }),
        ]);

        const formData = (formDataRaw as any)?.data || formDataRaw;
        const variablesData = (variablesDataRaw as any) || variablesDataRaw;

        console.log("[TaskPage] Data fetched:", { formData, variablesData });
        setTaskForm(formData);
        setTaskVariables(variablesData);

        if (formData?.processInstanceId) {
          setStoredProcInstId((prev) => prev || formData.processInstanceId);
        }

        if (formData?.nup) {
          setNup((prev) => prev || formData.nup);
        }
      } catch (error) {
        console.error("[TaskPage] Error fetching task data:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [taskId, user?.tenant, refreshTrigger]);

  const handleBack = () => {
    const prevParams = search.fromParams
      ? decodeNavParams(search.fromParams) || {}
      : {};

    if (search.from === "tasks") {
      navigate({ to: "/tasks", search: prevParams as any });
    } else if (search.from === "all-tasks") {
      navigate({ to: "/tasks/all", search: prevParams as any });
    } else {
      navigate({
        to: "/processes",
        search:
          Object.keys(prevParams).length > 0
            ? (prevParams as any)
            : { page: 1, size: 10 },
      });
    }
  };

  const handleNextTask = async (currentProcessInstanceId: string) => {
    setLoadingNext(true);
    try {
      const tenant = user?.tenant || "default-domain";

      const nextTask = await findNextTask({
        tenant: tenant,
        processInstanceId: currentProcessInstanceId,
        user: user,
        currentTaskId: taskId,
      });

      if (nextTask && nextTask.id) {
        if (nextTask.id) {
          navigate({
            to: "/tasks/$taskId/execute",
            params: { taskId: nextTask.id },
            search: { ...search, processInstanceId: currentProcessInstanceId },
            replace: true,
          });
        } else {
          handleBack();
        }
      } else {
        handleBack();
      }
    } catch (error) {
      console.error("Error fetching next task:", error);
      handleBack();
    }
  };

  const handleFormSubmit = async (
    formData: any,
    isSignatureTask: boolean = false,
    procInstId: string = "",
  ) => {
    setStoredProcInstId(procInstId);
    const variables = formData?.variables || formData || {};

    if (isSignatureTask) {
      setSignatureData(variables);
      setIsLoadingSignature(true);
      setSignatureError(null);
      try {
        setIsPasswordModalOpen(true);
        setIsLoadingSignature(false);
      } catch (e) {
        console.error("Error preparing signature:", e);
        setSignatureError(t("tasks.signatureError"));
        setIsLoadingSignature(false);
      }
      return;
    }

    if (!user?.tenant) {
      setErrorMessage(t("tasks.noTenant"));
      return;
    }

    try {
      await completeTaskV2(
        user.tenant,
        taskId,
        { variables },
        {
          headers: {
            "x-api-key": "7b9nBSEbAYorYo0os2vpW3CaHrg8A3jaceA5eqik" as any,
          },
        },
      );

      const finalProcInstId =
        procInstId || storedProcInstId || search.processInstanceId;
      if (finalProcInstId) {
        handleNextTask(finalProcInstId);
      } else {
        handleBack();
      }
    } catch (e: any) {
      console.error("Submission failed", e);
      const msg = e.body?.message || e.message || t("tasks.completeError");
      setErrorMessage(msg);
    }
  };

  const handleResendOtp = async (data: any, _isSignatureTask: boolean) => {
    setSignatureData(data?.variables || data || {});
    setIsResendOtpModalOpen(true);
  };

  const onExecuteSignature = async (password: string) => {
    if (!user?.tenant) return;
    setIsLoadingSignature(true);
    setSignatureError(null);
    try {
      await signAndCompleteTaskOtp(
        user.tenant,
        taskId,
        {
          variables: signatureData || {},
          otp: password,
        },
        {
          headers: {
            "x-api-key": "7b9nBSEbAYorYo0os2vpW3CaHrg8A3jaceA5eqik" as any,
          },
        },
      );

      setIsPasswordModalOpen(false);
      if (storedProcInstId) {
        handleNextTask(storedProcInstId);
      } else {
        handleBack();
      }
    } catch (e: any) {
      console.error("Signature failed", e);
      setSignatureError(t("tasks.invalidCode"));
    } finally {
      setIsLoadingSignature(false);
    }
  };

  const onResendOtpSubmit = async (email: string) => {
    if (!user?.tenant) return;
    setIsLoadingSignature(true);
    setSignatureError(null);
    try {
      await resendOtpTask(user.tenant, taskId, {
        variables: signatureData || {},
        email: email,
      });

      setSnackbar({
        open: true,
        message: "OTP reenviado.",
        severity: "success",
      });
    } catch (e) {
      console.error("Resend OTP failed", e);
      setSignatureError(t("tasks.resendOtpError"));
    } finally {
      setIsLoadingSignature(false);
    }
  };

  const closeSnackbar = () => {
    setErrorMessage(null);
    setSignatureError(null);
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const handleForcedTermination = async (justification: string) => {
    if (!user?.tenant) return;
    setIsForcedTerminating(true);
    try {
      await forceCancelTask(user.tenant, taskId, { justification });
      
      queryClient.invalidateQueries({ queryKey: ["process-instances"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });

      setSnackbar({
        open: true,
        message: t("tasks.forceCancelSuccess", "Processo encerrado forçadamente com sucesso."),
        severity: "success",
      });
      setForcedTerminationOpen(false);

      setTimeout(() => {
        handleBack();
      }, 1500);
    } catch (e: any) {
      console.error("Forced termination failed", e);
      const msg =
        e.body?.message || e.message || t("tasks.forceCancelError", "Falha ao forçar encerramento.");
      setErrorMessage(msg);
    } finally {
      setIsForcedTerminating(false);
    }
  };

  if (loadingNext) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando próxima tarefa...
        </Typography>
      </Box>
    );
  }

  const activityName =
    taskForm?.activityName || task?.name || "Execução de Tarefa";
  const originalProcessName =
    taskForm?.originalProcessName || task?.processName;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ py: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: "1.5rem", sm: "2.125rem" },
              }}
            >
              {activityName}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={handleBack}
              >
                {t("tasks.myTasks")}
              </Typography>
              {" > "}
              {nup && storedProcInstId ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() =>
                    navigate({
                      to: "/processes/$processInstanceId/history",
                      params: { processInstanceId: storedProcInstId },
                    })
                  }
                >
                  {nup}
                </Typography>
              ) : nup ? (
                <span>{nup}</span>
              ) : null}
              {nup && originalProcessName && " "}
              {originalProcessName && <span>{originalProcessName}</span>}
            </Typography>
            {storedProcInstId && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() =>
                  navigate({
                    to: "/processes/$processInstanceId/history",
                    params: { processInstanceId: storedProcInstId },
                  })
                }
                sx={{ mt: 1, mr: 1 }}
              >
                {t("tasks.viewHistory")}
              </Button>
            )}
            {hasPermission("process.delete") && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setForcedTerminationOpen(true)}
                sx={{ mt: 1, mr: 1 }}
              >
                {t("tasks.forceCancel", "Finalizar Processo Forçadamente")}
              </Button>
            )}
          </Box>
          <Box
            sx={{ display: "flex", gap: 4, alignItems: "flex-start", mt: 1 }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: "block" }}
              >
                Início da etapa:
              </Typography>
              <Typography variant="body2">
                {task?.startTime
                  ? new Date(task.startTime).toLocaleString("pt-BR")
                  : taskForm?.activityStartTime
                    ? new Date(taskForm.activityStartTime).toLocaleString(
                        "pt-BR",
                      )
                    : "-"}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: "block" }}
              >
                Prazo final da etapa:
              </Typography>
              <Typography variant="body2">
                {task?.dueDate
                  ? new Date(task.dueDate).toLocaleString("pt-BR")
                  : taskForm?.activityEndTime
                    ? new Date(taskForm.activityEndTime).toLocaleString("pt-BR")
                    : "-"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ position: "relative" }}>
          {loadingData || !taskForm || !taskVariables ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TaskFormRenderer
              taskId={taskId}
              token={token || ""}
              user={user}
              taskForm={taskForm}
              taskVariables={taskVariables}
              onBack={handleBack}
              onSubmit={handleFormSubmit}
              onResendOtp={handleResendOtp}
            />
          )}
        </Box>
      </Container>

      {isPasswordModalOpen && (
        <PasswordModal
          open={isPasswordModalOpen}
          onClose={() => {
            closeSnackbar();
            setIsPasswordModalOpen(false);
          }}
          onSubmit={onExecuteSignature}
          isLoading={isLoadingSignature}
          error={signatureError}
          userEmail={user?.email || ""}
        />
      )}

      {isResendOtpModalOpen && (
        <ResendOtpModal
          open={isResendOtpModalOpen}
          onClose={() => {
            closeSnackbar();
            setIsResendOtpModalOpen(false);
          }}
          onSubmit={onResendOtpSubmit}
          isLoading={isLoadingSignature}
          error={signatureError}
        />
      )}

      {task && (
        <AssignTaskDialog
          open={assignTaskOpen}
          onClose={() => setAssignTaskOpen(false)}
          taskId={taskId}
          taskName={activityName}
          onSuccess={() => setRefreshTrigger((prev) => prev + 1)}
        />
      )}

      {forcedTerminationOpen && (
        <ForcedTerminationDialog
          open={forcedTerminationOpen}
          onClose={() => setForcedTerminationOpen(false)}
          onSubmit={handleForcedTermination}
          isLoading={isForcedTerminating}
          taskName={activityName}
        />
      )}

      {snackbar.open && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => closeSnackbar()}
        >
          <Alert
            onClose={() => closeSnackbar()}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}

      {errorMessage && (
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={5000}
          onClose={() => closeSnackbar()}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => closeSnackbar()}>
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
